/**
 * ehr#536: pure helpers for the chart's Messages tab (tabs/MessagesPanel.tsx), which shows and sends
 * in the client's PORTAL conversations (the ones the client sees). Staff-only talk about the client
 * stays in the Messaging app and never shows here.
 */

/** One of the client's portal conversations, as POST /contacts/:id/chat/session lists it. */
export interface PortalRoom {
  roomId: string;
  name: string | null;
  subject: string | null;
  channel: string;
  /** Newest message (else when the room was made), epoch ms. */
  lastTs: number | null;
  /** The signed-in staff member's membership. */
  membership: 'join' | 'invite' | null;
}

export interface MxContent {
  msgtype?: string;
  body?: string;
  url?: string;
}
export interface MxEvent {
  type: string;
  sender: string;
  event_id: string;
  origin_server_ts?: number;
  content?: MxContent;
}
export interface Attachment {
  kind: 'image' | 'file';
  name: string;
  mxc: string;
}
export type Sender = 'mine' | 'client' | 'staff';
export interface ChatMsg {
  id: string;
  from: Sender;
  at: number;
  text: string;
  att?: Attachment;
}

export const SUBJECT_MAX = 120;

/** Portal clients are `@c-<contactId>:server`; staff are `@u-<userId>:server`. */
export function senderKind(sender: string, myMxid: string): Sender {
  if (sender === myMxid) return 'mine';
  return sender.startsWith('@c-') ? 'client' : 'staff';
}

/** Synapse /messages (dir=b) chunk → chat messages, oldest first. */
export function toChatMsgs(chunk: MxEvent[] | undefined, myMxid: string): ChatMsg[] {
  return (chunk ?? [])
    .filter((e) => e.type === 'm.room.message')
    .reverse()
    .map((e) => {
      const c = e.content ?? {};
      const isMedia = (c.msgtype === 'm.image' || c.msgtype === 'm.file') && !!c.url;
      const att: Attachment | undefined = isMedia
        ? {
            kind: c.msgtype === 'm.image' ? 'image' : 'file',
            name: c.body || 'attachment',
            mxc: c.url!,
          }
        : undefined;
      return {
        id: e.event_id,
        from: senderKind(e.sender, myMxid),
        at: e.origin_server_ts ?? 0,
        text: att ? '' : c.body || '',
        att,
      };
    });
}

/** Most recent first (no activity last), as the picker shows them. */
export function sortRooms(rooms: PortalRoom[]): PortalRoom[] {
  return [...rooms].sort((a, b) => (b.lastTs ?? 0) - (a.lastTs ?? 0));
}

/** What a conversation is called in the picker: its subject, then who it's with. */
export function threadLabel(r: Pick<PortalRoom, 'name' | 'subject'>): string {
  const subject = r.subject?.trim();
  const name = r.name?.trim();
  if (subject && name && subject !== name) return `${subject} (${name})`;
  return subject || name || 'Conversation';
}

/** The subject check the server makes too: required, at most 120 characters. Null when fine. */
export function subjectProblem(raw: string): string | null {
  const s = raw.replace(/\s+/g, ' ').trim();
  if (!s) return 'Add a subject.';
  if (s.length > SUBJECT_MAX) return `Keep the subject to ${SUBJECT_MAX} characters.`;
  return null;
}

/** Put a newly started thread at the top of the list (replacing any copy of it). */
export function withNewRoom(rooms: PortalRoom[], room: PortalRoom): PortalRoom[] {
  return [room, ...rooms.filter((r) => r.roomId !== room.roomId)];
}

/**
 * messaging#62: POST …/chat/thread answers 502 THREAD_JOIN_FAILED with `details.roomId` when the
 * conversation was made but the server couldn't add the caller yet. That room id, else null.
 */
export function startedRoomId(code: string, details: unknown): string | null {
  if (code !== 'THREAD_JOIN_FAILED') return null;
  const id = (details as { roomId?: unknown } | null | undefined)?.roomId;
  return typeof id === 'string' && /^![^\s:]+:\S+$/.test(id) ? id : null;
}
